import { PdfExportManager } from "@/features/pdf-export";
import { Skeleton as EditorSkeleton } from "@/pages/editor/modules/editor/components/Skeleton";
import { EditorReadyContext } from "@/pages/editor/modules/editor/components/editor-ready-context";
import { BaseLayout } from "@components/layouts/base.layout";
import { LibraryLayout } from "@components/layouts/library.layout";
import { USER_TYPE } from "@features/authentication";
import { useAppDispatch } from "@store";
import { resetStory } from "@store/story";
import { Suspense, lazy, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import RoleGuard from "../providers/role-guard.provider";

// const Library = lazy(() => import("@/pages/stories-lib"));
const Editor = lazy(() => import("@/pages/editor"));
const VersionsRedirect = lazy(
  () => import("@/pages/editor/components/VersionsRedirect"),
);
const Templates = lazy(() => import("@pages/templates"));
const Stories = lazy(() => import("@pages/stories"));
const CreateVersionBuilder = lazy(
  () => import("@/pages/create-version-builder"),
);
const BulkCreate = lazy(() => import("@/pages/bulk-create"));

const EditorRoute = () => {
  const { storyId, versionId } = useParams();
  return <EditorRouteInner key={`${storyId}-${versionId}`} />;
};

const EditorRouteInner = () => {
  const [isReady, setIsReady] = useState(false);
  const dispatch = useAppDispatch();

  // The story slice is global. Without this, a remount (the key above) mounted
  // the next document's editor over the previous document's store, and every
  // save that fired before the loader finished saved the wrong one. Cleanup
  // runs before the next key's effects, so the new editor always starts from
  // an empty store, exactly like a hard reload.
  useEffect(
    () => () => {
      dispatch(resetStory());
    },
    [dispatch],
  );

  return (
    <EditorReadyContext.Provider value={() => setIsReady(true)}>
      {!isReady && <EditorSkeleton />}
      <Suspense fallback={null}>
        <Editor />
      </Suspense>
      <PdfExportManager />
    </EditorReadyContext.Provider>
  );
};

const EDITOR_ROUTES = {
  path: "editor",
  children: [
    {
      path: "",
      index: true,
      element: (
        <LibraryLayout hideHeader>
          <Stories />
        </LibraryLayout>
      ),
    },
    {
      path: "folder/:folderId",
      element: (
        <LibraryLayout hideHeader>
          <Stories />
        </LibraryLayout>
      ),
    },
    {
      // One story's versions. The static "story" segment keeps this from
      // colliding with the ":storyId" editor route below.
      path: "story/:storyId/versions",
      element: (
        <LibraryLayout hideHeader>
          <Stories />
        </LibraryLayout>
      ),
    },
    {
      // Send flow: the full-screen create-version builder (off → redirects).
      path: "story/:storyId/create-version",
      element: (
        <Suspense fallback={null}>
          <CreateVersionBuilder />
        </Suspense>
      ),
    },
    {
      // Send flow: the full-screen bulk create (off → redirects).
      path: "story/:storyId/bulk-create",
      element: (
        <Suspense fallback={null}>
          <BulkCreate />
        </Suspense>
      ),
    },
    {
      path: "templates",
      element: (
        <RoleGuard notAllowed={[USER_TYPE.USER, USER_TYPE.CREATOR_LITE]}>
          <LibraryLayout title="Templates">
            <Templates />
          </LibraryLayout>
        </RoleGuard>
      ),
    },
    {
      path: ":storyId/versions",
      element: (
        <BaseLayout>
          <VersionsRedirect />
        </BaseLayout>
      ),
    },
    {
      path: ":storyId/versions/:versionId",
      element: (
        <BaseLayout>
          <VersionsRedirect />
        </BaseLayout>
      ),
    },
    {
      path: ":storyId/:versionId",
      element: <EditorRoute />,
    },
    {
      path: ":storyId",
      element: <EditorRoute />,
    },
  ],
};

export default EDITOR_ROUTES;
